/**
 * Store visual builder (Phase 2) — preview URL and local draft storage until
 * `/api/store/builder/*` lands (Phase 3).
 *
 * Env (Quasar / Vite):
 * - STORE_BUILDER_PREVIEW_URL — full preview page URL (default: same-origin `/store/preview`)
 */

export const STORE_BUILDER_DRAFT_STORAGE_KEY = 'blu_store_builder_draft_home_v1'

export { STORE_BUILDER_FONT_PAIRS } from './storeBuilderFontPairs'

export const STORE_BUILDER_BUTTON_SHAPES = [
  { value: 'square', labelKey: 'storeBuilder.siteStyle.buttonSquare' },
  { value: 'rounded', labelKey: 'storeBuilder.siteStyle.buttonRounded' },
  { value: 'pill', labelKey: 'storeBuilder.siteStyle.buttonPill' },
]

export const STORE_BUILDER_BUTTON_VARIANTS = [
  { value: 'solid', labelKey: 'storeBuilder.siteStyle.buttonVariantSolid' },
  { value: 'outline', labelKey: 'storeBuilder.siteStyle.buttonVariantOutline' },
  { value: 'flat', labelKey: 'storeBuilder.siteStyle.buttonVariantFlat' },
  { value: 'elevated', labelKey: 'storeBuilder.siteStyle.buttonVariantElevated' },
  { value: 'gloss', labelKey: 'storeBuilder.siteStyle.buttonVariantGloss' },
]

export const STORE_BUILDER_BUTTON_LABEL_COLORS = [
  { value: 'auto', labelKey: 'storeBuilder.siteStyle.buttonLabelAuto' },
  { value: 'light', labelKey: 'storeBuilder.siteStyle.buttonLabelLight' },
  { value: 'dark', labelKey: 'storeBuilder.siteStyle.buttonLabelDark' },
]

export const STORE_BUILDER_CORNER_TREATMENTS = [
  { value: 'sharp', labelKey: 'storeBuilder.siteStyle.cornerSharp' },
  { value: 'soft', labelKey: 'storeBuilder.siteStyle.cornerSoft' },
]

export const STORE_BUILDER_SPACING_DENSITIES = [
  { value: 'compact', labelKey: 'storeBuilder.siteStyle.spacingCompact' },
  { value: 'comfortable', labelKey: 'storeBuilder.siteStyle.spacingComfortable' },
  { value: 'spacious', labelKey: 'storeBuilder.siteStyle.spacingSpacious' },
]

export const STORE_BUILDER_CONTENT_WIDTHS = [
  { value: 'narrow', labelKey: 'storeBuilder.siteStyle.widthNarrow' },
  { value: 'standard', labelKey: 'storeBuilder.siteStyle.widthStandard' },
  { value: 'wide', labelKey: 'storeBuilder.siteStyle.widthWide' },
]

/** Curated site-style swatches (empty value = theme / preset default). */
export const STORE_SITE_COLOR_PRESETS = [
  { value: '', labelKey: 'storeBuilder.siteStyle.colorDefault' },
  { value: '#111827', labelKey: 'storeBuilder.siteStyle.colorInk' },
  { value: '#1e3a5f', labelKey: 'storeBuilder.siteStyle.colorNavy' },
  { value: '#2563eb', labelKey: 'storeBuilder.siteStyle.colorBlue' },
  { value: '#0d9488', labelKey: 'storeBuilder.siteStyle.colorTeal' },
  { value: '#15803d', labelKey: 'storeBuilder.siteStyle.colorGreen' },
  { value: '#b45309', labelKey: 'storeBuilder.siteStyle.colorAmber' },
  { value: '#be123c', labelKey: 'storeBuilder.siteStyle.colorRose' },
  { value: '#ffffff', labelKey: 'storeBuilder.siteStyle.colorWhite' },
  { value: '#f8fafc', labelKey: 'storeBuilder.siteStyle.colorCloud' },
  { value: '#f5f0e8', labelKey: 'storeBuilder.siteStyle.colorSand' },
]

export const STORE_BUILDER_HERO_LAYOUTS = [
  { value: 'split', labelKey: 'storeBuilder.section.heroLayoutSplit' },
  { value: 'stack', labelKey: 'storeBuilder.section.heroLayoutStack' },
]

export const STORE_BUILDER_HERO_FOCAL_POINTS = [
  { value: 'center', labelKey: 'storeBuilder.section.focalCenter' },
  { value: 'top', labelKey: 'storeBuilder.section.focalTop' },
  { value: 'bottom', labelKey: 'storeBuilder.section.focalBottom' },
  { value: 'left', labelKey: 'storeBuilder.section.focalLeft' },
  { value: 'right', labelKey: 'storeBuilder.section.focalRight' },
]

export const STORE_BUILDER_SOCIAL_ICON_KEYS = [
  { value: '', labelKey: 'storeBuilder.chrome.socialIconNone' },
  { value: 'instagram', labelKey: 'storeBuilder.chrome.socialIconInstagram' },
  { value: 'facebook', labelKey: 'storeBuilder.chrome.socialIconFacebook' },
  { value: 'tiktok', labelKey: 'storeBuilder.chrome.socialIconTiktok' },
  { value: 'pinterest', labelKey: 'storeBuilder.chrome.socialIconPinterest' },
  { value: 'youtube', labelKey: 'storeBuilder.chrome.socialIconYoutube' },
  { value: 'x', labelKey: 'storeBuilder.chrome.socialIconX' },
]

export function createDefaultSiteStyle () {
  return {
    primary_color: '',
    accent_color: '',
    background_color: '',
    header_color: '',
    text_color: '',
    font_pair: 'inter',
    button_shape: 'rounded',
    button_variant: 'solid',
    button_label_color: 'auto',
    corner_treatment: 'soft',
    spacing_density: 'comfortable',
    content_width: 'standard',
    enable_scroll_reveal: false,
    enable_add_to_cart_fly: false,
  }
}

export function createDefaultChrome () {
  return {
    nav_links: [],
    footer_columns: [],
    policy_links: [],
    social_links: [],
    announcement: {
      enabled: false,
      text: '',
      style_mode: 'theme',
      background_color: '',
      text_color: '',
      link_label: '',
      link_href: '',
      marquee: false,
      dismissible: false,
    },
    footer_wave_enabled: false,
  }
}

/** Quasar Material icons for section gallery (plan §2.2). */
export const STORE_SECTION_GALLERY_ICONS = {
  hero: 'campaign',
  featured_products: 'inventory_2',
  product_carousel: 'view_carousel',
  featured_collections: 'category',
  image_text: 'photo_library',
  rich_text: 'article',
  faq_accordion: 'quiz',
  image_row: 'view_column',
  product_details: 'shopping_bag',
}

export function resolveStoreBuilderPreviewUrl () {
  const fromEnv =
    import.meta.env.STORE_BUILDER_PREVIEW_URL ||
    import.meta.env.QCLI_STORE_BUILDER_PREVIEW_URL
  if (fromEnv && typeof fromEnv === 'string' && fromEnv.trim()) {
    return fromEnv.trim().replace(/\/$/, '')
  }
  if (typeof window !== 'undefined') {
    return `${window.location.origin}/store/preview`
  }
  return '/store/preview'
}

export function resolveStoreBuilderPreviewOrigin () {
  try {
    return new URL(resolveStoreBuilderPreviewUrl()).origin
  } catch {
    return typeof window !== 'undefined' ? window.location.origin : ''
  }
}
